import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/src/ui/layout.js";
import { createElement } from 'react';
import rehype2react from 'rehype-react';
import Doc from 'unified-doc';
import visit from 'unist-util-visit';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3 {...{
      "id": "overview"
    }}>{`Overview`}</h3>
    <p>{`Recipes for best ways to use the API methods exposed by a `}<inlineCode parentName="p">{`doc`}</inlineCode>{` instance.`}</p>
    <h3 {...{
      "id": "doccompile"
    }}><inlineCode parentName="h3">{`doc.compile()`}</inlineCode></h3>
    <p>{`Use this method if you want to compile and `}<strong parentName="p">{`render`}</strong>{` the document as in this example of compiling to `}<inlineCode parentName="p">{`react`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import { createElement } from 'react';
// import rehype2react from 'rehype-react';
// import Doc from 'unified-doc';

function() {
  const doc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: '> **some** markdown content',
    filename: 'doc.md',
  });
  return (
    <div>
      <div>Render the doc anywhere in the React tree</div>
      <div>{doc.compile().result}</div>
    </div>
  );
}
`}</code></pre>
    <h3 {...{
      "id": "docfile"
    }}><inlineCode parentName="h3">{`doc.file()`}</inlineCode></h3>
    <p>{`Use this method if you want to create or work with the corresponding JS `}<inlineCode parentName="p">{`File`}</inlineCode>{`, filename, extensions, and mime type.  This method is also useful for exporting to other supported formats.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import Doc from 'unified-doc';

function() {
  const doc = Doc({
    content: '> **some** markdown content',
    filename: 'doc.md',
  });

  const fileData = doc.file('.html');
  const { content, name, type } = fileData;
  let file = {};
  if (typeof File !== 'undefined') {
    file = new File([content], name, { type });
  }
  console.log('------------------------------');
  console.log('[Recipes] doc.file()')
  console.log('------------------------------');
  console.log('filename: ', fileData.name);
  console.log('extension: ', fileData.extension);
  console.log('stem: ', fileData.stem);
  console.log('mimeType: ', fileData.type);
  console.log('file: ', file);

  return <div>Check console for logged file</div>;
}
`}</code></pre>
    <h3 {...{
      "id": "docparse"
    }}><inlineCode parentName="h3">{`doc.parse()`}</inlineCode></h3>
    <p>{`Use this method if you want to obtain the `}<a parentName="p" {...{
        "href": "https://github.com/syntax-tree/hast"
      }}>{`hast`}</a>{` representation of the content, which is useful if you want to work with the syntax tree directly (e.g. with hast utils).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import Doc from 'unified-doc';
// import visit from 'unist-util-visit';

function() {
  const doc = Doc({
    content: '> **some** markdown content',
    filename: 'doc.md',
  });

  const hast = doc.parse();

  function visitor(hast) {
    console.log('visiting the text nodes of a hast tree...')
    visit(hast, 'text', node => {
      console.log('text node value: ', node.value);
    });
  }

  console.log('------------------------------');
  console.log('[Recipes] doc.parse()');
  console.log('------------------------------');
  console.log('hast: ', hast);
  visitor(hast);

  return <div>Check console for logged content</div>;
}
`}</code></pre>
    <h3 {...{
      "id": "docsearch"
    }}><inlineCode parentName="h3">{`doc.search()`}</inlineCode></h3>
    <p>{`Use this method if you want to search for matches for in the document for a given query and attached search algorithm.  Search results are simple and conveniently return start/end offset and snippet information that is useful to display context around the matched search value.`}</p>
    <p>{`This method is useful when returning interface-compatible data for `}<inlineCode parentName="p">{`marks`}</inlineCode>{` to highlight matches in a document.  It is also useful in general for computing offsets and derived data in other NLP pipelines and processes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import { createElement } from 'react';
// import rehype2react from 'rehype-react';
// import Doc from 'unified-doc';

function() {
  const term = 'content';
  const content = '> **some** markdown content, content, content';
  const filename = 'doc.md';

  const results = Doc({ content, filename }).search(term);
  const marks = results.map((result, i) => ({
    id: i.toString(),
    ...result,
  }));

  const doc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content,
    filename,
    marks,
  });

  return (
    <div>
      <div>
        <h4>Compiled</h4>
        <div>{doc.compile().result}</div>
        <h4>Results</h4>
        {results.map((result, i) => {
          const { start, end, snippet } = result;
          const [left, matched, right] = snippet;
          return (
            <div key={i}>
              {left}<strong>{matched}</strong>{right}{' '}
              (start: {start}, end: {end})
            </div>
          );
        })}
      </div>
    </div>
  );
}
`}</code></pre>
    <h3 {...{
      "id": "doctextcontent"
    }}><inlineCode parentName="h3">{`doc.textContent()`}</inlineCode></h3>
    <p>{`Use this method if you want to obtain the `}<inlineCode parentName="p">{`textContent`}</inlineCode>{` representation of the document, useful for computing offsets and derived data in other NLP pipelines and processes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import Doc from 'unified-doc';

function() {
  const term = 'content';
  const content = '> **some** markdown content, content, content';
  const filename = 'doc.md';
  const doc = Doc({ content, filename });
  const textContent = doc.textContent();

  function countPipeline(text, term) {
    return (text.match(new RegExp(\`\${term}\`, 'gi')) || []).length;
  }

  return (
    <div>
      <div>
        <strong>Text content:</strong>{' '}
        <code>{textContent}</code>
      </div>
      <div>
        <strong>Term:</strong>{' '}
        <code>{term}</code>
      </div>
      <div>
        <strong>Count:</strong>{' '}
        <code>{countPipeline(textContent, term)}</code>
      </div>
    </div>
  );
}
`}</code></pre>
    {
      /* Definitions */
    }

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      